import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldX, ArrowLeft, Home } from 'lucide-react'
import { usePermissions } from '../hooks/usePermissions'
import { Button } from '../components/ui/Button'
import { ErrorState } from '../components/ui/ErrorState'

/**
 * Rôle backend → clé i18n. Carte statique explicite : un libellé ne se dérive
 * jamais de la valeur d'enum. `SUPERADMIN` manquait à la version précédente,
 * qui affichait alors la chaîne brute « SUPERADMIN », en capitales, dans une
 * UI française.
 */
const ROLE_KEYS: Record<string, string> = {
  SUPERADMIN: 'roles.superAdmin',
  ADMIN: 'roles.admin',
  AGENT: 'roles.agent',
  USER: 'roles.user',
}

/**
 * LOT-H10 (DA-016). Second des deux écrans hors échelle : H1 à 48 px, trois
 * blocs de texte en français codé en dur (§7), et surtout un fond de page peint
 * à la main — `min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900`,
 * c'est-à-dire l'anti-patron exact de DA-013 : la page repeignait le fond du
 * châssis avec un dégradé décalé, d'où une couture visible.
 *
 * Aucun fond n'est peint ici. La page ne décide plus que de ce qu'elle dit.
 */
export default function UnauthorizedPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  const { user } = usePermissions()

  const roleKey = user?.role ? ROLE_KEYS[user.role] : undefined

  return (
    <ErrorState
      icon={ShieldX}
      /* `danger` et non `neutral` : ici, quelque chose est réellement refusé —
         c'est la frontière du §5sexies. Une 404 est une adresse qui n'existe
         pas ; un refus d'accès est un blocage. Et le rouge ne teinte que le
         glyphe, jamais le texte. */
      tone="danger"
      title={t('errors.unauthorized.title')}
      description={t('errors.unauthorized.description')}
      detail={
        <>
          {roleKey && <>{t('errors.unauthorized.role', { role: t(roleKey) })}</>}
          {roleKey && ' · '}
          {t('errors.unauthorized.contactAdmin')}
        </>
      }
      actions={
        <>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate(-1)}>
            {t('actions.back')}
          </Button>
          <Button variant="primary" icon={Home} onClick={() => navigate('/analytics')}>
            {t('actions.backToDashboard')}
          </Button>
        </>
      }
    />
  )
}
